import {
  AddOutlined,
  DeleteOutlined,
  EditOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useConfirm from "../../../hooks/useConfirm";
import { useQuery } from "../../../hooks/useQuery";
import useToast from "../../../hooks/useToast";
import { LocalisationService } from "../../../services/candidature/LocalisationService";
import LocalisationDialogForm from "./localisation/LocalisationDialogForm";

const ChoixLocalisation = forwardRef((props, ref) => {
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedIdLocalisation, setSelectedIdLocalisation] = useState(null);
  const [localisationList, setLocalisationList] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const { showToast, Toast } = useToast();
  const url = useQuery();
  const idType = url.get("id_type");

  const handleFetchLocalisationList = useCallback(() => {
    setFormLoading(true);
    LocalisationService.lister({ id_type: idType })
      .then((res) => {
        if (res && res.data) {
          setLocalisationList(res.data);
        }
      })
      .finally(() => setFormLoading(false));
  }, [idType]);

  const handleRefresh = useCallback(() => {
    handleFetchLocalisationList();
  }, [handleFetchLocalisationList]);

  const handleOpenForm = (idLocalisation = null) => {
    setSelectedIdLocalisation(idLocalisation);
    setOpenFormDialog(true);
  };

  const handlecloseForm = () => {
    setOpenFormDialog(false);
    setSelectedIdLocalisation(null);
    handleRefresh();
  };

  const handleDeleteLocalisation = (idLocalisation) => {
    showToast(TYPE_NOTIFICATION.INFO, "Suppression en cours...");
    LocalisationService.delete({
      id_choix_localisation: idLocalisation,
      id_type: idType,
    }).then((res) => {
      if (res && res.success) {
        showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
        handleRefresh();
      }
    });
  };

  const handleConfirmDeleteLocalisation = (idLocalisation) => {
    openConfirmDialog(
      "Confirmer la suppression",
      "Êtes-vous sûr de vouloir supprimer ce choix de localisation ?",
      () => {
        handleDeleteLocalisation(idLocalisation);
      }
    );
  };

  useImperativeHandle(ref, () => ({
    save: async () => true,
  }));

  useEffect(() => {
    handleFetchLocalisationList();
  }, [handleFetchLocalisationList]);

  return (
    <>
      <Toast />
      <DialogComponent />
      <LocalisationDialogForm
        onFormClose={handlecloseForm}
        open={openFormDialog}
        id_choix_localisation={selectedIdLocalisation}
        id_type={idType}
      />

      <Box sx={{ width: "100%", p: { xs: 1.5, md: 2 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.main",
                    backgroundColor: "#EAF2FF",
                    flexShrink: 0,
                  }}
                >
                  <PlaceOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="overline" color="primary" fontWeight={700}>
                    Paramétrage de l'AMI
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    Choix de localisation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ajoutez les lots ou zones géographiques concernés.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`${localisationList.length} localisation${
                    localisationList.length > 1 ? "s" : ""
                  }`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                <Tooltip title="Actualiser les données">
                  <span>
                    <IconButton color="primary" onClick={handleRefresh} disabled={formLoading}>
                      <RefreshOutlined />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddOutlined />}
                  onClick={() => handleOpenForm(null)}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Ajouter
                </Button>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            {formLoading && (
              <Grid container spacing={1.5}>
                {[1, 2, 3].map((item) => (
                  <Grid item xs={12} key={item}>
                    <Skeleton variant="rectangular" height={74} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))}
              </Grid>
            )}

            {!formLoading && localisationList.length === 0 && (
              <Alert
                severity="info"
                variant="outlined"
                action={
                  <Button
                    color="primary"
                    size="small"
                    startIcon={<AddOutlined />}
                    onClick={() => handleOpenForm(null)}
                  >
                    Ajouter
                  </Button>
                }
                sx={{
                  alignItems: "center",
                  borderRadius: 2,
                  backgroundColor: "#F8FAFC",
                }}
              >
                Aucun choix de localisation n'est encore renseigné. Cette étape
                est facultative, mais vous pouvez définir les zones concernées par l'AMI.
              </Alert>
            )}

            {!formLoading && localisationList.length > 0 && (
              <Stack spacing={1.25}>
                {localisationList.map((localisation, index) => (
                  <Paper
                    key={localisation.id_choix_localisation || index}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: "#FFFFFF",
                      transition: "border-color 160ms ease, box-shadow 160ms ease",
                      "&:hover": {
                        borderColor: "primary.light",
                        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1.5}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            backgroundColor: "#F1F5F9",
                            flexShrink: 0,
                          }}
                        >
                          <PlaceOutlinedIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            Localisation #{index + 1}
                          </Typography>
                          <Typography variant="body1" fontWeight={700}>
                            {localisation.nom_lot || "Localisation non spécifiée"}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Modifier">
                          <IconButton
                            color="primary"
                            onClick={() =>
                              handleOpenForm(localisation.id_choix_localisation)
                            }
                          >
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            color="error"
                            onClick={() =>
                              handleConfirmDeleteLocalisation(
                                localisation.id_choix_localisation
                              )
                            }
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
});

export default ChoixLocalisation;
